/**
 * A branch's review page, from the session's changed menu.
 *
 * A review is a Review app record (`dataType: 'review'`) pinned to a source
 * branch. One open review per branch: a second click opens the first, so its
 * comments and verdict are not split across two pages. Matched by the catalog
 * title the Review app gives a pinned record, then confirmed on the record
 * itself, so only those few are hydrated. A review someone retitled by hand is
 * missed and a new one is made; that costs a duplicate, not a wrong page.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import {
  type AppSummary,
  createDocument,
  projectDocumentsOfType,
  projectSources,
  readDocumentData,
} from '@mod/core/model'
import { sessionChangesContribution } from '../../services/apps/sessionChangesContribution'
import { personChangeMessage } from '../../services/identity/localPerson'

/** The title the Review app's start form gives a branch (StartForm.jsx). */
export function reviewSummary(branch: string): string {
  return branch.replace('mod/session/', 'session ')
}

/** The base as the Review app wants it: a remote ref, since nothing fetches a
 *  local one. */
export function reviewTarget(base: string): string {
  const bare = base.replace(/^refs\/heads\//, '').replace(/^origin\//, '')
  return `origin/${bare}`
}

const FIND_MS = 3000

async function loadWithin(
  repo: Repo,
  id: string,
  ms: number,
): Promise<unknown | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), ms)
  })
  const load = (async () => {
    const handle = await repo.find(id as AnyDocumentId)
    await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
    return handle.doc() as unknown
  })().catch(() => null)
  try {
    return await Promise.race([load, late])
  } finally {
    clearTimeout(timer)
  }
}

export async function openBranchReview(
  repo: Repo,
  projectId: string,
  projectDoc: unknown,
  installed: readonly AppSummary[],
  sourceId: string,
  branch: string,
  base: string,
): Promise<string> {
  const owner = sessionChangesContribution(installed)
  if (owner?.dataType !== 'review' || owner.action !== 'reviewBranch')
    throw new Error('Review is not installed with session changes')
  const sources = projectSources(projectDoc)
  if (!sources.sources.some((source) => source.id === sourceId))
    throw new Error('The branch source is not in this project')
  const summary = reviewSummary(branch)
  const candidates = projectDocumentsOfType(projectDoc, 'review')
    .filter((ref) => ref.title === summary)
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
  for (const ref of candidates) {
    // A catalog entry whose record never synced is unavailable, and a find
    // can wait a long time to say so. Skip it: a page that opens beats one
    // pinned to a record nobody can load.
    const doc = await loadWithin(repo, ref.id, FIND_MS)
    if (!doc) continue
    const data = readDocumentData(doc) as Record<string, unknown>
    const status = String(data.status ?? 'open')
    // A review from before sources had ids belongs to the default source only
    // while the project has one source. With several, the default may have
    // changed since, so such a review matches none and a fresh one is made.
    const recorded =
      data.sourceId ??
      (sources.sources.length === 1 ? sources.defaultSourceId : undefined)
    const sameSource = recorded !== undefined && String(recorded) === sourceId
    if (
      sameSource &&
      String(data.sourceBranch ?? '') === branch &&
      status === 'open'
    )
      return ref.id
  }
  const handle = await createDocument(repo, projectId, {
    ...personChangeMessage(),
    path: `review/${Math.random().toString(36).slice(2, 10)}`,
    title: summary,
    mime: 'application/mod-instance',
    dataType: 'review',
    data: {
      sourceId,
      sourceBranch: branch,
      targetBranch: reviewTarget(base),
      summary,
      verdict: 'open',
      status: 'open',
    },
  })
  return handle.documentId as string
}
